import { reactive } from 'vue';
import type { MethodDelta } from '../model.js';
import { figureField, GLOBAL_METRICS } from './metric-explanations.js';
import { DEFAULT_SCORE_METHOD, methodLabel, type ScoreMethod } from './metrics-series.js';
import { rawValue } from './version-series.js';

/**
 * The reading every screen shows the product figures in (product owner, 2026-09-27): normal, or
 * «Métricas globales» — every figure over the whole dataset. One choice for the whole platform,
 * kept per user on the server; `useGlobalMetrics` fills this from there and writes it back.
 *
 * Reactive, so any computed that reads a figure through `productFigure` follows the choice: the
 * hub, the group's card and executions, the groups' cards and comparisons, the configurations'
 * executions — all at once, without each screen asking.
 */
export const metricsReading = reactive<{ global: boolean; method: ScoreMethod }>({
  global: false,
  /**
   * The preferred score method (product owner, 2026-10-06), chosen once in the hub's options and
   * shown on every screen: no screen picks its own. Kept per user on the server like the reading;
   * `usePreferredMethod` fills it from there and writes it back. The hub's coherence box is the one
   * place both methods show side by side, to compare them; the configuration's rules map shows
   * both too, because there the difference between them is the point.
   */
  method: DEFAULT_SCORE_METHOD,
});

/**
 * One product figure of a method block, in the reading in force: `incoherences_detected_pct`,
 * `false_positives_pct` or `correct_pct`. Null for a block that has none.
 *
 * A screen that shows both readings side by side — the comparison of two executions — reads them
 * explicitly with `rawValue(block, figureField(key, global))` instead.
 */
export function productFigure(
  block: Partial<MethodDelta> | null | undefined,
  metricKey: string,
): number | null {
  if (!block) return null;

  return rawValue(block as MethodDelta, figureField(metricKey, metricsReading.global));
}

/**
 * The block of the preferred method out of figures kept per method — an execution's, a control's,
 * a group's last measurement —; undefined when it has none, which is «—» on screen and never another
 * method's figure.
 */
export function preferredBlock<T>(
  methods: Readonly<Record<string, T>> | null | undefined,
): T | undefined {
  return methods?.[metricsReading.method];
}

/** One product figure of figures kept per method, under the method and the reading in force. */
export function preferredFigure(
  methods: Readonly<Record<string, Partial<MethodDelta>>> | null | undefined,
  metricKey: string,
): number | null {
  return productFigure(preferredBlock(methods), metricKey);
}

/**
 * What the figures on screen are read with, for a caption or an export that leaves the screen:
 * «Alerta mayor», or «Alerta mayor · Métricas globales».
 */
export function readingLabel(): string {
  return readingText(metricsReading.method, metricsReading.global);
}

/**
 * The same caption for a method and a reading a screen holds itself — a prop, a CSV's header —:
 * «Resta calculada», or «Resta calculada · Métricas globales».
 */
export function readingText(method: string, global: boolean): string {
  const label = methodLabel(method);

  return global ? `${label} · ${GLOBAL_METRICS}` : label;
}
